import {inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {environment} from '@environments/environment.prod';
import {ClientHttpPort} from '@shared/application/ports/http/client.http.port';
import {LocalStoragePort} from '@shared/application/ports/storage/local.storage.port';
import {NavigationPort} from '@shared/application/ports/navigation/navigation.port';
import {ProductPort} from '@features/product/application/ports/product.port';
import {
  CreateProductPayloadDto
} from '@features/product/application/use_cases/create-product/dto/create-product.payload.dto';
import {
  CreateProductResponseDto
} from '@features/product/application/use_cases/create-product/dto/create-product.response.dto';
import {CreateProductHttpMapper} from '@features/product/infrastructure/http/create-product/create-product.http.mapper';
import {
  CreateProductHttpPayloadDto
} from '@features/product/infrastructure/http/create-product/create-product.http.payload.dto';
import {
  CreateProductHttpResponseDto
} from '@features/product/infrastructure/http/create-product/create-product.http.response.dto';
import {
  DeleteProductPayloadDto
} from '@features/product/application/use_cases/delete-product/dto/delete-product.payload.dto';
import {
  DeleteProductResponseDto
} from '@features/product/application/use_cases/delete-product/dto/delete-product.response.dto';
import {DeleteProductHttpMapper} from '@features/product/infrastructure/http/delete-product/delete-product.http.mapper';
import {
  DeleteProductHttpPayloadDto
} from '@features/product/infrastructure/http/delete-product/delete-product.http.payload.dto';
import {
  DeleteProductHttpResponseDto
} from '@features/product/infrastructure/http/delete-product/delete-product.http.response.dto';
import {
  FindOneProductPayloadDto
} from '@features/product/application/use_cases/find-one-product/dto/find-one-product.payload.dto';
import {
  FindOneProductResponseDto
} from '@features/product/application/use_cases/find-one-product/dto/find-one-product.response.dto';
import {
  FindOneProductHttpMapper
} from '@features/product/infrastructure/http/find-one-product/find-one-product.http.mapper';
import {
  FindOneProductHttpPayloadDto
} from '@features/product/infrastructure/http/find-one-product/find-one-product.http.payload.dto';
import {
  FindOneProductHttpResponseDto
} from '@features/product/infrastructure/http/find-one-product/find-one-product.http.response.dto';
import {
  HiddenProductPayloadDto
} from '@features/product/application/use_cases/hidden-product/dto/hidden-product.payload.dto';
import {
  HiddenProductResponseDto
} from '@features/product/application/use_cases/hidden-product/dto/hidden-product.response.dto';
import {HiddenProductHttpMapper} from '@features/product/infrastructure/http/hidden-product/hidden-product.http.mapper';
import {
  HiddenProductHttpPayloadDto
} from '@features/product/infrastructure/http/hidden-product/hidden-product.http.payload.dto';
import {
  HiddenProductHttpResponseDto
} from '@features/product/infrastructure/http/hidden-product/hidden-product.http.response.dto';
import {ListProductPayloadDto} from '@features/product/application/use_cases/list-product/dto/list-product.payload.dto';
import {
  ListProductResponseDto
} from '@features/product/application/use_cases/list-product/dto/list-product.response.dto';
import {ListProductHttpMapper} from '@features/product/infrastructure/http/list-product/list-product.http.mapper';
import {
  ListProductHttpPayloadDto
} from '@features/product/infrastructure/http/list-product/list-product.http.payload.dto';
import {
  ListProductHttpResponseDto
} from '@features/product/infrastructure/http/list-product/list-product.http.response.dto';
import {
  SetDefaultProductPayloadDto
} from '@features/product/application/use_cases/set-default-product/dto/set-default-product.payload.dto';
import {
  SetDefaultProductResponseDto
} from '@features/product/application/use_cases/set-default-product/dto/set-default-product.response.dto';
import {
  SetDefaultProductHttpMapper
} from '@features/product/infrastructure/http/set-default-product/set-default-product.http.mapper';
import {
  SetDefaultProductHttpPayloadDto
} from '@features/product/infrastructure/http/set-default-product/set-default-product.http.payload.dto';
import {
  SetDefaultProductHttpResponseDto
} from '@features/product/infrastructure/http/set-default-product/set-default-product.http.response.dto';
import {ShowProductPayloadDto} from '@features/product/application/use_cases/show-product/dto/show-product.payload.dto';
import {
  ShowProductResponseDto
} from '@features/product/application/use_cases/show-product/dto/show-product.response.dto';
import {ShowProductHttpMapper} from '@features/product/infrastructure/http/show-product/show-product.http.mapper';
import {
  ShowProductHttpPayloadDto
} from '@features/product/infrastructure/http/show-product/show-product.http.payload.dto';
import {
  ShowProductHttpResponseDto
} from '@features/product/infrastructure/http/show-product/show-product.http.response.dto';
import {
  UpdateProductPayloadDto
} from '@features/product/application/use_cases/update-product/dto/update-product.payload.dto';
import {
  UpdateProductResponseDto
} from '@features/product/application/use_cases/update-product/dto/update-product.response.dto';
import {UpdateProductHttpMapper} from '@features/product/infrastructure/http/update-product/update-product.http.mapper';
import {
  UpdateProductHttpPayloadDto
} from '@features/product/infrastructure/http/update-product/update-product.http.payload.dto';
import {
  UpdateProductHttpResponseDto
} from '@features/product/infrastructure/http/update-product/update-product.http.response.dto';
import {FilterInterface} from '@core/interfaces/filter/filter.interface';
import {HttpHeaders} from '@angular/common/http';
import {QueryHttpService} from '@shared/infrastructure/http/services/query-http.service';
import {
  FindByIdProductPayloadDto
} from '@features/product/application/use_cases/find-by-id-product/dto/find-by-id-product.payload.dto';
import {
  FindByIdProductResponseDto
} from '@features/product/application/use_cases/find-by-id-product/dto/find-by-id-product.response.dto';
import {
  FindByIdProductHttpPayloadDto
} from '@features/product/infrastructure/http/find-by-id-product/find-by-id-product.http.payload.dto';
import {
  FindByIdProductHttpResponseDto
} from '@features/product/infrastructure/http/find-by-id-product/find-by-id-product.http.response.dto';
import {
  FindByIdProductHttpMapper
} from '@features/product/infrastructure/http/find-by-id-product/find-by-id-product.http.mapper';

@Injectable()
export class ProductHttpAdapter implements ProductPort {
  private url = environment.uri;
  private readonly http = inject(ClientHttpPort);
  private readonly queryService: QueryHttpService = inject(QueryHttpService);

  constructor(
    private readonly localStoragePort: LocalStoragePort,
    private readonly navigationPort: NavigationPort,
  ) {
  }

  public createProduct(payload: CreateProductPayloadDto): Observable<CreateProductResponseDto> {
    return this.http.post<CreateProductHttpPayloadDto, CreateProductHttpResponseDto>
    (`${this.url}product-register`, CreateProductHttpMapper.toInfra(payload))
      .pipe(
        map((responseHttp) =>
          CreateProductHttpMapper.toResponse(responseHttp)
        )
      );
  }

  public deleteProduct(payload: DeleteProductPayloadDto): Observable<DeleteProductResponseDto> {
    return this.http.post<DeleteProductHttpPayloadDto, DeleteProductHttpResponseDto>(`${this.url}delete-product`, DeleteProductHttpMapper.toInfra(payload)).pipe(map((responseHttp) => DeleteProductHttpMapper.toResponse(responseHttp)));
  }

  public findOneProduct(payload: FindOneProductPayloadDto): Observable<FindOneProductResponseDto> {
    return this.http.post<FindOneProductHttpPayloadDto, FindOneProductHttpResponseDto>(`${this.url}find-one-product`, FindOneProductHttpMapper.toInfra(payload)).pipe(map((responseHttp) => FindOneProductHttpMapper.toResponse(responseHttp)));
  }

  public findByIdProduct(payload: FindByIdProductPayloadDto): Observable<FindByIdProductResponseDto | null> {
    const url = window.location.href;
    const {suffix} = this.navigationPort.extractQueryParams(url);
    let suffixDatabase: string | null = null;
    if (!suffix) {
      suffixDatabase = this.localStoragePort.getByKey<string | null>('_suffix');
      if (!suffixDatabase) throw new Error(`Could not find suffix`);
    } else {
      suffixDatabase = suffix;
    }
    const filter: FilterInterface = {
      singleQuery: ["id", "=", `${payload.id}`]
    };
    filter.withRelationships = [
      "discounts",
      "insurance",
      "untaxed_cost",
      "taxes_cost",
      "signature_template",
      "scoring",
      "otherCosts"
    ];
    const headers: HttpHeaders = new HttpHeaders({
      "token": environment.secret_key,
      "x-filter-model": JSON.stringify(filter),
    });
    return this.http.get<FindByIdProductHttpResponseDto>
    (`${this.url}product-list?suffix_database=${suffixDatabase}`, {headers})
      .pipe(map((responseHttp) => FindByIdProductHttpMapper.toResponse(responseHttp)));
  }

  public hiddenProduct(payload: HiddenProductPayloadDto): Observable<HiddenProductResponseDto> {
    return this.http.get<HiddenProductHttpResponseDto>(`${this.url}product-hidden/${payload.id}`)
      .pipe(map((responseHttp) => HiddenProductHttpMapper.toResponse(responseHttp)));
  }

  public listProduct(payload: ListProductPayloadDto): Observable<ListProductResponseDto> {
    const url = window.location.href;
    const {suffix} = this.navigationPort.extractQueryParams(url);
    let suffixDatabase: string | null = null;
    if (!suffix) {
      suffixDatabase = this.localStoragePort.getByKey<string | null>('_suffix');
      if (!suffixDatabase) throw new Error(`Could not find suffix`);
    } else {
      suffixDatabase = suffix;
    }
    const httpPayload = ListProductHttpMapper.toInfra(payload);
    const filter: FilterInterface = this.queryService.toQueryFilterModel(payload);
    filter.withRelationships = [
      "discounts",
      "insurance",
      "untaxed_cost",
      "taxes_cost",
      "signature_template",
      "scoring",
      "otherCosts",
      "creditDocumentRequirements.creditDocumentType",
    ];
    if (httpPayload.limit) {
      filter["paginate"] = httpPayload.limit;
    }
    const headers: HttpHeaders = new HttpHeaders({
      "token": environment.secret_key,
      "x-filter-model": JSON.stringify(filter),
    });
    const pageParam = httpPayload.page ? `&page=${httpPayload.page}` : '';
    return this.http.get<ListProductHttpResponseDto>
    (`${this.url}product-list?suffix_database=${suffixDatabase}${pageParam}${payload.page ? ('&page=' + payload.page) : ('')}`, {headers})
      .pipe(
        map((responseHttp) =>
          ListProductHttpMapper.toResponse(responseHttp)
        )
      );
  }

  public setDefaultProduct(payload: SetDefaultProductPayloadDto): Observable<SetDefaultProductResponseDto> {
    return this.http.get<SetDefaultProductHttpResponseDto>(`${this.url}product-default/${payload.id}`)
      .pipe(map((responseHttp) => SetDefaultProductHttpMapper.toResponse(responseHttp)));
  }

  public showProduct(payload: ShowProductPayloadDto): Observable<ShowProductResponseDto> {
    return this.http.get<ShowProductHttpResponseDto>(`${this.url}product-active/${payload.id}`)
      .pipe(map((responseHttp) => ShowProductHttpMapper.toResponse(responseHttp)));
  }

  public updateProduct(payload: UpdateProductPayloadDto): Observable<UpdateProductResponseDto> {
    const payloadHttp: UpdateProductHttpPayloadDto = UpdateProductHttpMapper.toInfra(payload);
    return this.http.post<UpdateProductHttpPayloadDto, UpdateProductHttpResponseDto>(`${this.url}products/${payload.id}`, payloadHttp)
      .pipe(map((responseHttp) => UpdateProductHttpMapper.toResponse(responseHttp)));
  }
}
